import { type ComponentType, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { useNewsUnreadCount } from '@/features/feedNews/useNewsUnreadCount'
import { branding } from '@/shared/config/branding'
import { cn } from '@/shared/lib/cn'
import {
  isSettingsSubmenuRoute,
  parseSettingsGrupo,
  SETTINGS_GRUPO_PARAM,
  SETTINGS_SUBMENU_ITEMS,
  type SidebarMenuItem,
} from '@/pages/settings/settingsPageConfig'
import { Button } from '@/shared/ui/Button'
import { GuardedNavLink } from '@/shared/ui/GuardedNavLink'
import {
  CalculatorIcon,
  CalendarIcon,
  CalendarClockIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileTextIcon,
  FlaskIcon,
  HomeIcon,
  PackageIcon,
  PersonStandingIcon,
  RssIcon,
  SettingsIcon,
  SparklesIcon,
  UsersIcon,
} from '@/shared/ui/icons'
import { AccountMenu } from '@/widgets/navigation/AccountMenu'
import { getSidebarNavEntries, type AppNavEntry } from '@/widgets/navigation/appNavigationCatalog'
import { GoToSearchTrigger } from '@/widgets/navigation/GoToSearchTrigger'

interface SidebarNavigationProps {
  collapsed: boolean
  onToggle: () => void
  onOpenGoTo: () => void
}

const NAV_ENTRY_ICONS: Record<string, ComponentType<{ size?: number }>> = {
  dashboard: HomeIcon,
  documentos: FileTextIcon,
  patients: UsersIcon,
  plantoes: CalendarClockIcon,
  'chat-ia': SparklesIcon,
  noticias: RssIcon,
  calendar: CalendarIcon,
  'last-results': FlaskIcon,
  anatomy: PersonStandingIcon,
  inventory: PackageIcon,
  calculator: CalculatorIcon,
}

const navLinkClass = (collapsed: boolean, isActive: boolean, nested = false) =>
  cn(
    'group flex w-full items-center gap-3 rounded-2xl py-3 text-sm font-semibold transition-colors',
    nested ? 'px-3 pl-4' : 'px-3',
    isActive
      ? 'bg-[var(--color-mint-soft)] text-[var(--color-text-primary)]'
      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-page)]',
    collapsed && !nested && 'justify-center px-2',
    nested && collapsed && 'justify-center px-2 pl-2',
  )

const NavGroupLabel = ({ children, collapsed }: { children: string; collapsed: boolean }) => {
  if (collapsed) {
    return (
      <li
        className="mx-auto my-2 h-px w-6 bg-[var(--color-border)]"
        aria-hidden
        title={children}
      />
    )
  }
  return (
    <li className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.1em] text-[var(--color-text-muted)]">
      {children}
    </li>
  )
}

type SidebarItem = {
  to: string
  label: string
  icon: ComponentType<{ size?: number }>
  badge?: number
  state?: unknown
}

const SidebarLink = ({ item, collapsed }: { item: SidebarItem; collapsed: boolean }) => {
  const badge = item.badge && item.badge > 0 ? item.badge : null
  const label = badge ? `${item.label}, ${badge} não lidas` : item.label
  return (
    <li>
      <GuardedNavLink
        to={item.to}
        state={item.state}
        aria-label={label}
        title={label}
        className={({ isActive }) => cn(navLinkClass(collapsed, isActive), collapsed && 'relative')}
      >
        <item.icon size={20} />
        {!collapsed ? <span className="min-w-0 flex-1">{item.label}</span> : null}
        {badge ? (
          <span
            aria-hidden
            className={cn(
              'min-w-5 rounded-full bg-[var(--color-mint-strong)] px-1.5 text-center text-[11px] font-bold leading-5 text-white tabular-nums',
              collapsed && 'absolute right-1 top-1',
            )}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </GuardedNavLink>
    </li>
  )
}

const navEntryToSidebarItem = (
  entry: AppNavEntry,
  badge?: number,
): SidebarItem => ({
  to: entry.to,
  label: entry.label,
  icon: NAV_ENTRY_ICONS[entry.id] ?? HomeIcon,
  badge,
  state: entry.state,
})

export const SidebarNavigation = ({ collapsed, onToggle, onOpenGoTo }: SidebarNavigationProps) => {
  const role = useSessionStore((s) => s.user?.role ?? 'doctor')
  const user = useSessionStore((s) => s.user)
  const isSystemAdmin = user?.isSystemAdmin ?? false
  const navCtx = useMemo(() => ({ role, isSystemAdmin }), [role, isSystemAdmin])
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const activeGrupo = parseSettingsGrupo(searchParams.get(SETTINGS_GRUPO_PARAM))

  const isOnSettingsSubmenu = isSettingsSubmenuRoute(location.pathname)
  const [settingsExpanded, setSettingsExpanded] = useState(() =>
    isSettingsSubmenuRoute(location.pathname),
  )
  const [prevIsOnSettingsSubmenu, setPrevIsOnSettingsSubmenu] = useState(isOnSettingsSubmenu)
  if (isOnSettingsSubmenu !== prevIsOnSettingsSubmenu) {
    setPrevIsOnSettingsSubmenu(isOnSettingsSubmenu)
    if (!isOnSettingsSubmenu) setSettingsExpanded(false)
  }

  const settingsSubmenuOpen = settingsExpanded || isOnSettingsSubmenu

  const settingsSubmenuItems = useMemo(
    () =>
      SETTINGS_SUBMENU_ITEMS.filter(
        (m) =>
          (!m.adminOnly || isSystemAdmin) && (!m.doctorOnly || role === 'doctor'),
      ),
    [isSystemAdmin, role],
  )

  const displayName = user?.name?.trim() || 'Perfil'
  const crm = user?.crm?.trim()
  const newsUnread = useNewsUnreadCount()
  const primaryItems = useMemo(
    () =>
      getSidebarNavEntries('primary', navCtx).map((entry) =>
        navEntryToSidebarItem(entry, entry.id === 'noticias' ? newsUnread : undefined),
      ),
    [navCtx, newsUnread],
  )
  const moreItems = useMemo(
    () => getSidebarNavEntries('more', navCtx).map((entry) => navEntryToSidebarItem(entry)),
    [navCtx],
  )

  const isCategoryActive = (item: SidebarMenuItem) => {
    if (location.pathname !== '/settings') return false
    if (!item.grupo) return !activeGrupo
    return activeGrupo === item.grupo
  }

  const settingsHubActive =
    location.pathname === '/settings' &&
    !settingsSubmenuItems.some((item) => item.grupo && isCategoryActive(item))

  return (
    <aside
      className={cn(
        'hidden border-r border-[var(--color-border)] bg-white/85 p-3 pt-6 backdrop-blur-md [data-theme=dark]:bg-[var(--color-surface)]/90 md:fixed md:left-0 md:top-[calc(36px+env(safe-area-inset-top,0px))] md:bottom-0 md:z-10 md:flex md:flex-col md:gap-3 md:h-[calc(100dvh-36px-env(safe-area-inset-top,0px))] md:min-h-0',
        collapsed ? 'md:w-[92px]' : 'md:w-[248px]',
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center',
          collapsed ? 'justify-center' : 'gap-2 px-2',
        )}
      >
        {!collapsed ? (
          <div className="min-w-0 flex-1">
            <span
              className="block truncate bg-gradient-to-r from-[var(--color-text-primary)] via-[var(--color-mint-strong)] to-[var(--color-mint)] bg-clip-text text-xl font-bold tracking-tight text-transparent"
              aria-label={branding.appName}
            >
              {branding.appName}
            </span>
          </div>
        ) : null}
        <Button
          variant="ghost"
          onClick={onToggle}
          className="h-10 min-h-10 w-10 shrink-0 rounded-xl p-0"
          aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
        >
          {collapsed ? <ChevronRightIcon size={18} aria-hidden /> : <ChevronLeftIcon size={18} aria-hidden />}
        </Button>
      </div>

      <div className="shrink-0 px-0.5 pb-2">
        <GoToSearchTrigger variant="sidebar" collapsed={collapsed} onOpen={onOpenGoTo} />
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto" aria-label="Menu principal">
        <ul className="space-y-0.5">
          {primaryItems.map((item) => (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ))}

          <NavGroupLabel collapsed={collapsed}>Mais</NavGroupLabel>
          {moreItems.map((item) => (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ))}

          <li aria-hidden className="mx-3 my-2 h-px bg-[var(--color-border)]" />
          <li>
            <GuardedNavLink
              to="/settings"
              onClick={() => setSettingsExpanded(true)}
              aria-label="Configurações"
              title="Configurações"
              className={({ isActive }) =>
                navLinkClass(collapsed, isActive || settingsHubActive || isOnSettingsSubmenu)
              }
            >
              <SettingsIcon size={20} />
              {!collapsed ? (
                <>
                  <span className="min-w-0 flex-1">Configurações</span>
                  <ChevronDownIcon
                    size={18}
                    className={cn(
                      'shrink-0 transition-transform',
                      settingsSubmenuOpen ? 'rotate-180' : 'rotate-0',
                    )}
                    aria-hidden
                  />
                </>
              ) : null}
            </GuardedNavLink>
            {settingsSubmenuOpen && !collapsed ? (
              <ul className="mt-1 space-y-0.5 border-l-2 border-[var(--color-border)] pl-2 ml-4">
                {settingsSubmenuItems.map((sub) => (
                  <li key={sub.to}>
                    <GuardedNavLink
                      to={sub.to}
                      state={sub.state}
                      aria-label={sub.label}
                      title={sub.label}
                      className={() =>
                        navLinkClass(
                          collapsed,
                          location.pathname === '/settings'
                            ? isCategoryActive(sub)
                            : false,
                          true,
                        )
                      }
                    >
                      <sub.icon size={18} />
                      <span className="min-w-0 flex-1 whitespace-normal leading-snug text-xs">
                        {sub.label}
                      </span>
                    </GuardedNavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        </ul>
      </nav>

      <div className="mt-auto shrink-0 border-t border-[var(--color-border)] pt-3">
        <AccountMenu
          variant="sidebar"
          collapsed={collapsed}
          name={displayName}
          email={user?.email}
          avatarUrl={user?.avatarUrl}
          crm={crm}
          role={role}
          isSystemAdmin={isSystemAdmin}
        />
      </div>
    </aside>
  )
}
